import React from 'react';
import PropTypes from 'prop-types';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import X from 'lucide-react/dist/esm/icons/x';
import { cn } from '~/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, style, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn('fixed inset-0 bg-black/60 backdrop-blur-md', className)}
		// z-index above the legacy global Modal (100) so dialogs surface above already-open
		// modals; the same as DialogContent so DOM order decides, and a nested dialog's
		// overlay dims the dialog that opened it.
		style={{ zIndex: 201, ...style }}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
DialogOverlay.propTypes = { className: PropTypes.string, style: PropTypes.object };

// The legacy global Modal's backdrop, for dialogs that stand in for one.
const MODAL_OVERLAY_STYLE = { backgroundColor: 'hsl(var(--black) / 0.75)', backdropFilter: 'none' };

const DialogContent = React.forwardRef(({
	className, children, showClose = false, style, overlayStyle, ...props
}, ref) => (
	<DialogPortal>
		<DialogOverlay style={overlayStyle} />
		<DialogPrimitive.Content
			ref={ref}
			className={cn(
				'fixed left-1/2 top-1/2 w-full max-w-lg transform -translate-x-1/2 -translate-y-1/2 rounded-xs bg-card text-card-foreground border border-border shadow-2xl overflow-hidden flex flex-col focus:outline-none',
				className
			)}
			style={{ maxHeight: 'calc(100% - 3.5rem)', zIndex: 201, ...style }}
			{...props}
		>
			{children}
			{showClose && (
				<DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-transparent">
					<X className="h-4 w-4" />
					<span className="sr-only">Close</span>
				</DialogPrimitive.Close>
			)}
		</DialogPrimitive.Content>
	</DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;
DialogContent.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	showClose: PropTypes.bool,
	style: PropTypes.object,
	overlayStyle: PropTypes.object
};

const DialogHeader = ({ className, ...props }) => (
	<div className={cn('px-8 pt-6 text-left', className)} {...props} />
);
DialogHeader.propTypes = { className: PropTypes.string };

const DialogFooter = ({ className, ...props }) => (
	<div className={cn('flex items-center justify-end gap-2 px-8 pb-7', className)} {...props} />
);
DialogFooter.propTypes = { className: PropTypes.string };

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
	<DialogPrimitive.Title ref={ref} className={cn('text-2xl font-medium text-foreground', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
DialogTitle.propTypes = { className: PropTypes.string };

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
	<DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
DialogDescription.propTypes = { className: PropTypes.string };

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogTrigger,
	DialogClose,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
	MODAL_OVERLAY_STYLE
};
